import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { EmptyState } from "./empty-state"
import { cn } from "@/lib/utils"

export interface Column<T> {
  header: string
  cell: (row: T) => React.ReactNode
  className?: string
}

interface CursorPageInfo {
  total: number
  hasNextPage: boolean
  endCursor?: string | null
}

interface CursorCallbacks {
  page: number
  canGoPrev: boolean
  goNext: (endCursor: string) => void
  goPrev: () => void
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyFn: (row: T) => string
  pageInfo?: { total: number; limit: number; offset: number }
  onPageChange?: (offset: number) => void
  cursorInfo?: CursorPageInfo
  cursorCallbacks?: CursorCallbacks
  pageSize?: number
  loading?: boolean
  emptyTitle?: string
  emptyDescription?: string
  onRowClick?: (row: T) => void
  selectable?: boolean
  selectedKeys?: Set<string>
  onSelectionChange?: (keys: Set<string>) => void
}

export function DataTable<T>({
  columns,
  data,
  keyFn,
  pageInfo,
  onPageChange,
  cursorInfo,
  cursorCallbacks,
  pageSize,
  loading,
  emptyTitle = "No data",
  emptyDescription,
  onRowClick,
  selectable,
  selectedKeys,
  onSelectionChange,
}: DataTableProps<T>) {
  const initialLoading = loading && data.length === 0
  const hasData = data.length > 0

  const allKeys = data.map(keyFn)
  const allSelected = selectable && allKeys.length > 0 && allKeys.every((k) => selectedKeys?.has(k))
  const someSelected = selectable && allKeys.some((k) => selectedKeys?.has(k))

  const toggleAll = () => {
    if (!onSelectionChange) return
    if (allSelected) {
      onSelectionChange(new Set())
    } else {
      onSelectionChange(new Set(allKeys))
    }
  }

  const toggleRow = (key: string) => {
    if (!onSelectionChange || !selectedKeys) return
    const next = new Set(selectedKeys)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onSelectionChange(next)
  }

  if (initialLoading) {
    return (
      <div className="rounded-lg glass-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border/30 hover:bg-transparent">
              {selectable && (
                <TableHead className="w-10 bg-muted/30">
                  <Skeleton className="h-4 w-4" />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead
                  key={col.header}
                  className={cn(
                    "text-xs uppercase tracking-wider text-muted-foreground font-medium bg-muted/30",
                    col.className,
                  )}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i} className="border-border/20">
                {selectable && (
                  <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.header} className={col.className}>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (!hasData && !loading) {
    return (
      <div className="rounded-lg glass-card">
        <EmptyState title={emptyTitle} description={emptyDescription} />
      </div>
    )
  }

  const useCursor = !!cursorInfo && !!cursorCallbacks
  const effectivePageSize = pageSize ?? pageInfo?.limit ?? 25

  const showPagination = useCursor
    ? cursorInfo.total > effectivePageSize
    : pageInfo && pageInfo.total > pageInfo.limit

  const paginationBar = (border: "top" | "bottom") => {
    if (!showPagination) return null

    if (useCursor) {
      const totalPages = Math.ceil(cursorInfo.total / effectivePageSize)
      const rangeStart = (cursorCallbacks.page - 1) * effectivePageSize + 1
      const rangeEnd = Math.min(cursorCallbacks.page * effectivePageSize, cursorInfo.total)

      return (
        <div
          className={cn(
            "flex items-center justify-between px-4 py-3",
            border === "top" ? "border-b border-border/20" : "border-t border-border/20",
          )}
        >
          <span className="text-xs text-muted-foreground tabular-nums">
            {rangeStart}&ndash;{rangeEnd} of {cursorInfo.total}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full"
              disabled={!cursorCallbacks.canGoPrev}
              onClick={() => cursorCallbacks.goPrev()}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground px-2 tabular-nums">
              {cursorCallbacks.page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-full"
              disabled={!cursorInfo.hasNextPage}
              onClick={() => {
                if (cursorInfo.endCursor) cursorCallbacks.goNext(cursorInfo.endCursor)
              }}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )
    }

    const totalPages = Math.ceil(pageInfo!.total / pageInfo!.limit)
    const currentPage = Math.floor(pageInfo!.offset / pageInfo!.limit) + 1

    return (
      <div
        className={cn(
          "flex items-center justify-between px-4 py-3",
          border === "top" ? "border-b border-border/20" : "border-t border-border/20",
        )}
      >
        <span className="text-xs text-muted-foreground tabular-nums">
          {pageInfo!.offset + 1}&ndash;{Math.min(pageInfo!.offset + pageInfo!.limit, pageInfo!.total)} of{" "}
          {pageInfo!.total}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full"
            disabled={pageInfo!.offset === 0}
            onClick={() => onPageChange?.(Math.max(0, pageInfo!.offset - pageInfo!.limit))}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground px-2 tabular-nums">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full"
            disabled={pageInfo!.offset + pageInfo!.limit >= pageInfo!.total}
            onClick={() => onPageChange?.(pageInfo!.offset + pageInfo!.limit)}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg glass-card overflow-hidden">
      {paginationBar("top")}
      <div className={cn("transition-opacity duration-150", loading && "opacity-50 pointer-events-none")}>
        <Table>
          <TableHeader>
            <TableRow className="border-border/30 hover:bg-transparent">
              {selectable && (
                <TableHead className="w-10 bg-muted/30">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </TableHead>
              )}
              {columns.map((col) => (
                <TableHead
                  key={col.header}
                  className={cn(
                    "text-xs uppercase tracking-wider text-muted-foreground font-medium bg-muted/30",
                    col.className,
                  )}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, i) => {
              const key = keyFn(row)
              const isSelected = selectedKeys?.has(key)
              return (
                <TableRow
                  key={key}
                  className={cn(
                    "border-border/20 transition-colors duration-150",
                    onRowClick ? "cursor-pointer hover:bg-accent/50" : undefined,
                    isSelected && "bg-primary/5",
                  )}
                  style={{ animationDelay: `${i * 30}ms` }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selectable && (
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleRow(key)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select row ${key}`}
                      />
                    </TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col.header} className={col.className}>
                      {col.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      {paginationBar("bottom")}
    </div>
  )
}
